// src/pages/HeathrowFareCompareLite.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import api from "../utils/api";
import "../styles/heathrowFareCompare.css";

const HEATHROW_PC = "TW6 1QG";
const ONE_HOUR_MS = 60 * 60 * 1000;

const HeathrowFareCompareLite = () => {
  const [postcode, setPostcode] = useState("");
  const [direction, setDirection] = useState("TO"); // TO = to Heathrow, FROM = from Heathrow

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const autoSubmitted = useRef(false);
  const [showMoreMarket, setShowMoreMarket] = useState(false);

  const MARKET_FLAGS = {
    heathrow_official: true,
    edisonlee: false,
    minicab: false,
    online_first: false,
    online_second: false,
    uber_reserve: false,
  };

  const defaultPickupDate = useMemo(
    () => new Date(Date.now() + ONE_HOUR_MS).toISOString(),
    []
  );

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setHasSubmitted(true);
    setResult(null);

    if (!postcode.trim()) {
      setError("Please enter a postcode.");
      return;
    }

    const pickup_postcode = direction === "TO" ? postcode.trim() : HEATHROW_PC;
    const dropoff_postcode = direction === "TO" ? HEATHROW_PC : postcode.trim();

    const payload = {
      pickup_postcode,
      dropoff_postcode,
      pickup_datetime: defaultPickupDate,
      passengers: 1,
      luggage: 0
    };

    try {
      setLoading(true);
      const res = await api.post("/api/fare-compare", payload);
      setResult(res.data);
    } catch (err) {
      console.error("❌ Fare compare error:", err);
      const msg =
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        "We could not generate a fare comparison for this route.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Prefill + autosubmit
  useEffect(() => {
    setPostcode("E1 6AN");
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (postcode) {
      autoSubmitted.current = true;
      handleSubmit(new Event("submit"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode]);

  const renderMoney = value =>
    typeof value === "number" ? `£${value.toFixed(2)}` : "—";

  const bandAbsOrPct = (mid, { abs = 1, pct = 0.08, threshold = 120 } = {}) => {
    if (mid == null || Number.isNaN(Number(mid))) return null;
    const m = Number(mid);
    if (m <= threshold) {
      return {
        min: Number((m - abs).toFixed(2)),
        mid: Number(m.toFixed(2)),
        max: Number((m + abs).toFixed(2)),
      };
    }
    return {
      min: Number((m * (1 - pct)).toFixed(2)),
      mid: Number(m.toFixed(2)),
      max: Number((m * (1 + pct)).toFixed(2)),
    };
  };

  const marketExtras = () => {
    const m = result?.fares?.market;
    if (!m) return [];
    const entries = [
      { key: "heathrow_official", label: "Heathrow official (saloon)", val: m.heathrow_official?.saloon },
      { key: "edisonlee", label: "Edison Lee (saloon)", val: m.edisonlee?.saloon },
      { key: "minicab", label: "Minicab (saloon)", val: m.minicab?.saloon },
      { key: "online_first", label: "Online taxi 1st (saloon)", val: m.online_taxi_first?.mpv },
      { key: "online_second", label: "Online taxi 2nd (saloon)", val: m.online_taxi_second?.mpv },
      { key: "uber_reserve", label: "Uber Reserve (saloon)", val: m.uber_reserve?.saloon },
    ];
    return entries
      .filter(e => MARKET_FLAGS[e.key])
      .map(e => ({ ...e, range: bandAbsOrPct(e.val) }))
      .filter(e => e.range);
  };

  const renderBlackCabRange = fares => {
    const bc = fares?.black_cab;
    if (!bc) return null;

    return (
      <p style={{ marginTop: 4, fontSize: 14 }}>
        <strong>Estimated range:</strong>{" "}
        <span className="provider-price">{renderMoney(bc.min)}</span> –{" "}
        <span className="provider-price">{renderMoney(bc.max)}</span>{" "}
        {bc.mid && (
          <span>
            (typical ~<span className="provider-price">{renderMoney(bc.mid)}</span>)
          </span>
        )}
      </p>
    );
  };

  const renderRideHailRange = fares => {
    const rh = fares?.ride_hail;
    if (!rh) return null;
    return (
      <p style={{ marginTop: 4, fontSize: 14 }}>
        <strong>Estimated range:</strong>{" "}
        <span className="provider-price">{renderMoney(rh.min)}</span> –{" "}
        <span className="provider-price">{renderMoney(rh.max)}</span>
      </p>
    );
  };

  const journeyDistanceText =
    result?.journey &&
    `Distance: ~${result.journey.distance_miles} miles (${result.journey.distance_km} km)`;

  const confidence = result?.summary?.confidence || "Low";
  const notes = result?.summary?.notes || [];
  const benchmarksCapturedAt = result?.fares?.benchmarks?.captured_at
    ? new Date(result.fares.benchmarks.captured_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
    : "—";
  const ccgVsBlackCabMid = result?.summary?.ccg_vs_blackcab_mid ?? null;
  const showBestValue = Boolean(result?.fares?.cheapcabgrab);
  const bestValueLine =
    ccgVsBlackCabMid != null && Math.abs(ccgVsBlackCabMid) < 5
      ? "🟡 Prices are similar — fixed fares avoid surge & meter risk"
      : "🟢 Best value on this route: CheapCabGrab (fixed fare)";

  return (
    <div className="tool-page">
      <Helmet>
        <title>
          Heathrow Fare Compare (Lite) | CheapCabGrab vs Uber vs Black Cab
        </title>

        <meta
          name="description"
          content="Fast Heathrow fare comparison: enter one postcode to see CheapCabGrab fixed fares vs Uber/ride-hail and black cab estimates."
        />
        <meta
          name="keywords"
          content="Heathrow taxi fare comparison, Heathrow taxi prices, London taxi vs Uber Heathrow, Heathrow cab cost, Heathrow taxi estimate, airport transfers"
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/heathrow-fare-compare-lite"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Heathrow Fare Compare (Lite) | CheapCabGrab"
        />
        <meta
          property="og:description"
          content="Quickly compare CheapCabGrab fixed fares vs ride-hail and black cab for Heathrow."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/heathrow-fare-compare-lite"
        />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Fare Compare (Lite)",
            url: "https://www.cheapcabgrab.com/heathrow-fare-compare-lite",
            description:
              "Fast Heathrow fare comparison: enter one postcode to see CheapCabGrab fixed fares vs ride-hail and black cab estimates.",
            about: [
              {
                "@type": "Service",
                name: "Heathrow Taxi",
                url: "https://www.cheapcabgrab.com/heathrow-taxi"
              },
              {
                "@type": "Service",
                name: "Airport Transfers",
                url: "https://www.cheapcabgrab.com"
              }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <main className="tool-container">
        {/* HEADER */}
        <header className="tool-header">
          <h1>Heathrow Fare Compare (Lite)</h1>
          <p className="tool-intro">
            Enter one postcode, choose direction, and instantly see fixed-price
            CheapCabGrab vs ride-hail and black cab estimates for Heathrow.
            For custom time/passengers/bags use the{" "}
            <Link to="/heathrow-fare-compare">full fare compare tool</Link>.
          </p>
        </header>

        <section className="tool-grid" style={{ gridTemplateColumns: "1fr" }}>
          {/* FORM CARD */}
          <article className="tool-card">
            <h2>Quick Heathrow Fare Check</h2>
            <p style={{ fontSize: 14, marginBottom: 10 }}>
              Defaults: pickup time = +1 hour, passengers = 1, luggage = 0.
              Swap direction if you&apos;re leaving Heathrow.
            </p>

            <form className="tool-form" onSubmit={handleSubmit}>
              <div className="tool-form-row">
                <label htmlFor="postcode">
                  Your postcode (non-Heathrow end)
                </label>
                <input
                  id="postcode"
                  type="text"
                  value={postcode}
                  onChange={e => setPostcode(e.target.value)}
                  placeholder="e.g. SL1 1XY"
                  autoComplete="off"
                />
              </div>

              <div className="tool-form-row tool-form-row-inline">
                <div>
                  <label>Direction</label>
                  <div className="direction-toggle">
                    <button
                      type="button"
                      className={direction === "TO" ? "active" : ""}
                      onClick={() => setDirection("TO")}
                    >
                      To Heathrow
                    </button>
                    <button
                      type="button"
                      className={direction === "FROM" ? "active" : ""}
                      onClick={() => setDirection("FROM")}
                    >
                      From Heathrow
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    Heathrow end is fixed at {HEATHROW_PC}
                  </p>
                </div>
              </div>

              {error && (
                <div className="tool-error" style={{ marginTop: 8 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="primary-btn"
                disabled={loading}
                style={{ marginTop: 6 }}
              >
                {loading ? "Comparing..." : "Compare Heathrow Fares"}
              </button>

              <p
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: "#555",
                  lineHeight: 1.5
                }}
              >
                Want full control (time, passengers, luggage)? Try the{" "}
                <Link to="/heathrow-fare-compare">full fare compare tool</Link>.
              </p>
            </form>
          </article>

          {/* RESULTS CARD */}
          <article className="tool-card tool-results-card">
            <h2>Fare Comparison Results</h2>

            {!hasSubmitted && (
              <p style={{ fontSize: 14 }}>
                Enter your postcode and direction to see typical fares for this Heathrow trip.
              </p>
            )}

            {hasSubmitted && !loading && !result && !error && (
              <p style={{ fontSize: 14 }}>
                No results yet. Please try again with a different postcode.
              </p>
            )}

            {result && (
              <>
                {/* Journey summary */}
                <div className="tool-summary">
                  <h3>Journey Summary</h3>
                  <p style={{ fontSize: 14 }}>
                    <strong>From:</strong> {result.journey.pickup_postcode}
                    <br />
                    <strong>To:</strong> {result.journey.dropoff_postcode}
                    <br />
                    {journeyDistanceText && (
                      <span>
                        <strong>{journeyDistanceText}</strong>
                      </span>
                    )}
                    <br />
                    <strong>Drive time:</strong>{" "}
                    {result.journey.duration_min != null
                      ? `${result.journey.duration_min} min`
                      : "—"}
                    <br />
                    <strong>Distance source:</strong>{" "}
                    {result.journey.distance_source || "—"}
                  </p>
                  <div style={{ marginTop: 8, fontSize: 13, color: "#555" }}>
                    <strong>Confidence:</strong> {confidence}
                    {notes.length > 0 && (
                      <ul
                        style={{
                          marginTop: 6,
                          marginBottom: 0,
                          paddingLeft: 18
                        }}
                      >
                        {notes.map((n, idx) => (
                          <li key={idx}>{n}</li>
                        ))}
                      </ul>
                    )}
                    {showBestValue && (
                      <p className="best-value-line">
                        {bestValueLine}
                      </p>
                    )}
                  </div>
                </div>

                {/* Providers grid */}
                <div className="tool-providers-grid" style={{ gridTemplateColumns: "1fr" }}>
                  {/* CheapCabGrab */}
                  {result.fares?.cheapcabgrab && (
                    <div className="tool-provider-card highlight">
                      <h3>CheapCabGrab (Fixed Fare)</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Estimated one-way fare:</strong>{" "}
                        <span className="provider-price ccg-price">
                          {renderMoney(
                            result.fares.cheapcabgrab.one_way
                          )}
                        </span>
                      </p>
                      {result.fares.cheapcabgrab.vehicle && (
                        <p style={{ fontSize: 13, marginTop: 2 }}>
                          Vehicle:{" "}
                          {result.fares.cheapcabgrab.vehicle
                            .charAt(0)
                            .toUpperCase() +
                            result.fares.cheapcabgrab.vehicle.slice(1)}
                        </p>
                      )}
                      <p style={{ fontSize: 13, marginTop: 6 }}>
                        <strong>Key points:</strong> fixed price, pre-booked,
                        licensed private hire driver. No meter, price agreed
                        before travel.
                      </p>
                      {typeof result.summary?.ccg_vs_blackcab_mid === "number" && (
                        <p style={{ fontSize: 13, marginTop: 6, color: "#0a7" }}>
                          CheapCabGrab is around £{result.summary.ccg_vs_blackcab_mid.toFixed(2)}{" "}
                          cheaper than a typical black cab estimate on this route.
                        </p>
                      )}
                      <p className="ccg-reassure">
                        ✔ Licensed drivers · ✔ Fixed price · ✔ No surge · ✔ Meet &amp; greet available
                      </p>
                      <p style={{ fontSize: 13, marginTop: 8 }}>
                        ✅ Lock this fixed price now — get an{" "}
                        <Link to="/">instant quote</Link> (no obligation)
                      </p>
                    </div>
                  )}

                  {/* Ride-hailing */}
                  {result.fares?.ride_hail && (
                    <div className="tool-provider-card">
                      <h3>Ride-hailing Apps (Uber/Bolt etc.)</h3>
                      {renderRideHailRange(result.fares)}
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        App pricing can surge and may include airport fees depending on provider.
                      </p>
                    </div>
                  )}

                  {/* Black cab */}
                  {result.fares?.black_cab && (
                    <div className="tool-provider-card">
                      <h3>London Taxi / Black Cab (Metered)</h3>
                      {renderBlackCabRange(result.fares)}
                      {result.fares.black_cab_note && (
                        <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                          {result.fares.black_cab_note}
                        </p>
                      )}
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        Metered fares vary with traffic and waiting time.
                      </p>
                    </div>
                  )}

                  {/* Airport taxi benchmark */}
                  {result.fares?.benchmarks?.airport_taxi?.saloon && (
                    <div className="tool-provider-card">
                      <h3>Airport Taxi (Benchmark)</h3>
                      <p style={{ marginTop: 4, fontSize: 14 }}>
                        <strong>Typical one-way (saloon):</strong>{" "}
                        <span className="provider-price benchmark-price">
                          {renderMoney(result.fares.benchmarks.airport_taxi.saloon.min)}
                        </span>{" "}
                        –{" "}
                        <span className="provider-price benchmark-price">
                          {renderMoney(result.fares.benchmarks.airport_taxi.saloon.max)}
                        </span>
                      </p>
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        Reference quote ({result.fares.benchmarks.match}) — last updated{" "}
                        {benchmarksCapturedAt}
                      </p>
                    </div>
                  )}

                  {/* Online taxi benchmark (collapsible) */}
                  {result.fares?.benchmarks?.online_taxi?.saloon && (
                    <div className="tool-provider-card">
                      <h3>Online Taxi (Benchmark)</h3>
                      <p style={{ marginTop: 4, fontSize: 14 }}>
                        <strong>Typical one-way (saloon):</strong>{" "}
                        <span className="provider-price benchmark-price">
                          {renderMoney(result.fares.benchmarks.online_taxi.saloon.min)}
                        </span>{" "}
                        –{" "}
                        <span className="provider-price benchmark-price">
                          {renderMoney(result.fares.benchmarks.online_taxi.saloon.max)}
                        </span>
                      </p>
                      <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                        Reference quote ({result.fares.benchmarks.match}) — last updated{" "}
                        {benchmarksCapturedAt}
                      </p>
                    </div>
                  )}

                  {/* More market prices (optional) */}
                  {result.fares?.market && marketExtras().length > 0 && (
                    <div className="tool-provider-card">
                      <div className="market-toggle">
                        <button type="button" onClick={() => setShowMoreMarket(s => !s)}>
                          {showMoreMarket ? "Hide more market prices ▲" : "Show more market prices ▾"}
                        </button>
                      </div>
                      {showMoreMarket && (
                        <div style={{ marginTop: 8 }}>
                          <h3>More market prices</h3>
                          {marketExtras().map((e, idx) => (
                            <p key={idx} style={{ marginTop: idx ? 8 : 4, fontSize: 14 }}>
                              <strong>{e.label}:</strong>{" "}
                              <span className="provider-price benchmark-price">
                                {renderMoney(e.range.min)}
                              </span>{" "}
                              –{" "}
                              <span className="provider-price benchmark-price">
                                {renderMoney(e.range.max)}
                              </span>
                            </p>
                          ))}
                          {result.fares.market_match && (
                            <p style={{ fontSize: 12, marginTop: 6, color: "#666" }}>
                              Source: DB match {result.fares.market_match}
                              {result.fares.market?.captured_at
                                ? ` • captured ${new Date(result.fares.market.captured_at).toLocaleString("en-GB")}`
                                : ""}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Disclaimer */}
                <div className="tool-disclaimer">
                  <p style={{ fontSize: 12, color: "#555" }}>
                    {result.disclaimer ||
                      "These fare ranges are estimates only and not live prices. Always confirm the exact fare in the taxi, app or booking system before travel."}
                  </p>
                </div>

              </>
            )}
          </article>
        </section>

        {/* REVIEWS AT BOTTOM (consistent with other tools) */}
        <section className="tool-section tool-explainer" style={{ marginTop: 24 }}>
          <h2>How Heathrow taxi fares are calculated</h2>
          <p>
            Taxi and airport transfer prices vary by distance, time of day, traffic, and each provider’s pricing model. For Heathrow trips, some operators use fixed prices agreed in advance, while others rely on meters or dynamic app-based pricing. We base our estimates on real road distance and typical journey times (not crow-fly distance), using routing data from services like OSRM to estimate how long a journey normally takes.
          </p>
          <p>
            Metered London taxis (black cabs) typically charge a flag-fall fee plus per-mile rates and time-based charges when stationary or moving slowly, so final fares rise during congestion. Ride-hailing apps can add surge pricing at busy times, and airport fees vary by provider. To add context, we compare results against recent market benchmarks from airport taxi operators and online booking platforms. These references are not live quotes, but they help show what travellers typically pay for similar Heathrow routes.
          </p>
          <h3>Why fixed fares can differ from metered taxis</h3>
          <p>
            Fixed-price airport transfers are calculated in advance based on expected distance, time, and vehicle type. Once booked, the price does not change because of traffic, delays, or route adjustments, making them predictable when timing matters. Metered and app-based services can look cheaper at first, but the final cost may increase due to congestion, waiting time, or high demand. Prices shown here are estimates to help you compare typical costs rather than guarantee a final fare.
          </p>
          <p style={{ marginBottom: 0 }}>
            Our tools continuously review routing data, traffic conditions, and historical pricing patterns to provide realistic comparisons. For full control over travel time, passengers, and luggage, use the full fare comparison tool for a more detailed breakdown.
          </p>
        </section>

        <section className="tool-section" style={{ marginTop: 32 }}>
          <h2>Customer Reviews</h2>
          <ReviewsBar />
        </section>
      </main>
    </div>
  );
};

export default HeathrowFareCompareLite;
